import { useCallback, useEffect, useState } from 'react';

import { Modal } from '@simplywallst/ui-core';
import { usePersistentStorage } from '@/hooks/usePersistentStorage';

import { useGeneratePortfolioLinkToken } from '../hooks/useGeneratePortfolioLinkToken';
import { PlaidPortfolioLinkingPortal } from './PlaidPortfolioLinkingPortal';

import { PLAID_LINK_TOKEN_KEY } from './constants';
import { createPortfolioLinkingSDKError } from './utils/createPortfolioLinkingSDKError';
import { getConnectionData } from './utils/getConnectionData';
import { useInitializePortfolioConnection } from '../hooks/useInitializePortfolioConnection';

import type {
  PlaidLinkError,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import type { PortfolioLinkingSDKError } from '../error/PortfolioLinkingSDKError';

type Props = {
  onError: (error?: PortfolioLinkingSDKError) => void;
  onClose: () => void;
  onSuccess: ({ portfolioId }: { portfolioId?: string }) => void;
  providerBrokerageId: string | null;
};

export const PlaidPortfolioConnector = ({
  onError: handleError,
  onSuccess: handleSuccess,
  onClose,
  providerBrokerageId,
}: Props) => {
  const [openPlaidLinkPortal, setOpenPlaidLinkPortal] = useState(false);
  const { set, flush } = usePersistentStorage();

  const { data, status } = useGeneratePortfolioLinkToken({
    provider: 'plaid',
    providerBrokerageId,
  });

  const { mutate } = useInitializePortfolioConnection();

  const isError = status === 'error';
  const isEmptyData = status === 'success' && !data;
  const isDataAvailable = status === 'success' && typeof data !== 'undefined';

  useEffect(() => {
    if (isError || isEmptyData) {
      handleError();
    }
  }, [isError, isEmptyData, handleError]);

  useEffect(() => {
    if (isDataAvailable) {
      setOpenPlaidLinkPortal(true);
      set(PLAID_LINK_TOKEN_KEY, data.linkToken);
    }
  }, [isDataAvailable, data?.linkToken, set]);

  const onSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      mutate(
        {
          connection: getConnectionData(publicToken, metadata),
          provider: 'plaid',
          platform: RUNTIME_ENV === 'native' ? 'native' : 'browser',
        },
        {
          onError: () => {
            handleError();
          },
          onSuccess: () => {
            handleSuccess({ portfolioId: data?.portfolioId });
          },
        }
      );
    },
    [data, handleError, handleSuccess, mutate]
  );

  const onError = useCallback(
    (error: PlaidLinkError) => {
      flush(PLAID_LINK_TOKEN_KEY);
      handleError(createPortfolioLinkingSDKError(error));
    },
    [flush, handleError]
  );

  if (status === 'pending') return <Modal isNaked isLoading />;

  return (
    <>
      {isDataAvailable && openPlaidLinkPortal && (
        <PlaidPortfolioLinkingPortal
          linkToken={data.linkToken}
          onSuccess={onSuccess}
          onError={onError}
          onClose={onClose}
        />
      )}
    </>
  );
};
